import { useEffect, useState } from 'react';
/**
 * The part of the layout viewport the on-screen keyboard covers, and the height left visible.
 *
 * The mobile app runs in a WebView that is never resized when the keyboard opens, so `window.innerHeight`
 * — and with it every `vh` unit and every percentage — keeps reporting the full screen. Only the visual
 * viewport shrinks. Anything pinned to the bottom of the layout viewport has to subtract `overlap` itself,
 * or it ends up underneath the keyboard.
 *
 * Size against `visibleHeight` and never against `vh` units or percentages. iOS sometimes resizes the
 * layout viewport for the keyboard and sometimes does not, and when it does, `vh` keeps resolving against
 * the old height — so `70vh` stays 612px inside a 498px viewport and overflows it.
 *
 * Pinch-zoom shrinks the visual viewport too, so a scaled page reports no overlap: the bottom of the
 * layout viewport is then off-screen for a reason that moving the UI cannot fix.
 *
 * @returns The covered height and the visible height, in CSS pixels.
 *
 * @example
 * const {overlap, visibleHeight} = useKeyboardOverlap();
 * // Keep a bottom-anchored sheet between the keyboard and the top safe area.
 * <Box
 *     position="fixed"
 *     bottom={`${overlap}px`}
 *     maxHeight={`calc(${visibleHeight}px - env(safe-area-inset-top))`}
 * />
 */
/**
 * offsetTop is deliberately no part of this. Reading it as a shift of the visible band is the obvious
 * interpretation and it is wrong here: iOS reports it while the keyboard is up without moving
 * fixed-position boxes, so subtracting it cancels the offset out and drops the box back behind the
 * keyboard. Measured on an iPhone 17 Pro, where the covered height and offsetTop are both ~376.
 */
const measureViewport = () => {
    const viewport = window.visualViewport;
    if (!viewport) {
        return { overlap: 0, visibleHeight: 0 };
    }
    const isZoomed = viewport.scale > 1;
    return {
        overlap: isZoomed ? 0 : Math.max(0, window.innerHeight - viewport.height),
        visibleHeight: isZoomed ? window.innerHeight : viewport.height,
    };
};
export const useKeyboardOverlap = () => {
    // Measured up front so a drawer opening while the keyboard is already up is not painted uncapped first.
    const [state, setState] = useState(measureViewport);
    useEffect(() => {
        const viewport = window.visualViewport;
        if (!viewport) {
            return;
        }
        const measure = () => {
            // A viewport event that changes nothing is common — panning a zoomed page reports the same
            // numbers every frame — so keep the previous object rather than re-rendering every consumer.
            setState(previous => {
                const next = measureViewport();
                return previous.overlap === next.overlap && previous.visibleHeight === next.visibleHeight
                    ? previous
                    : next;
            });
        };
        measure();
        // `scroll` matters as much as `resize`: iOS shifts the visual viewport rather than resizing it
        // when the focused field sits near the bottom of a long page.
        viewport.addEventListener('resize', measure);
        viewport.addEventListener('scroll', measure);
        return () => {
            viewport.removeEventListener('resize', measure);
            viewport.removeEventListener('scroll', measure);
        };
    }, []);
    return state;
};
